// ============================================================
// src/composables/useIdleTimer.js
// Tracks user activity and locks session after inactivity.
// ============================================================

import { ref, onMounted, onUnmounted, watch } from 'vue'
import store from '@/store'

/**
 * Utility function to check if JWT token is expired
 * @returns {boolean} true if token is expired or missing
 */
function isJwtExpired() {
  try {
    const token = sessionStorage.getItem('userToken')
    if (!token) return true

    // Parse JWT payload
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(base64))

    // Check if token has exp claim and if it's expired
    if (!payload.exp) return false // No expiry claim, assume valid

    const currentTime = Math.floor(Date.now() / 1000)
    const isExpired = payload.exp < currentTime

    return isExpired
  } catch (error) {
    return true // Assume expired on error
  }
}

function getIdleTimeout() {
  const userData = JSON.parse(sessionStorage.getItem("user"))
  const rawTimeout = userData?.data?.session_timeout
  let timeoutMinutes = parseInt(rawTimeout) || 30
  
  // Validate session_timeout: min 5 mins, max 480 mins (8 hours)
  if (isNaN(timeoutMinutes) || timeoutMinutes < 5) {
    timeoutMinutes = 5
  } else if (timeoutMinutes > 480) {
    timeoutMinutes = 480
  }
  
  return timeoutMinutes * 60 * 1000
}
// const timeoutMinutes = userData?.session_timeout || 30 

/**
 * @param {import('vue').Ref<boolean>} isAuthenticated - reactive ref indicating if user is logged in
 * @returns {{ isLocked: import('vue').Ref<boolean>, resetTimer: () => void }}
 */
export function useIdleTimer(isAuthenticated) {
  const isLocked = ref(false)
  let timerId = null

  // Only track "intentional" user actions, not passive ones like hover/scroll
  // Removed 'mousemove' (triggered by hover/animations) and 'scroll' (auto-scroll content)
  const EVENTS = ['keydown', 'mousedown', 'touchstart', 'click']

  function startTimer() {
    clearTimer()
    if (!isAuthenticated.value || isLocked.value) return
    
    // Check if token is already expired
    if (isJwtExpired()) {
      isLocked.value = true
      return
    }
    
    const timeout = getIdleTimeout()
    timerId = setTimeout(() => {
      isLocked.value = true
    }, timeout)
  }
  

  function clearTimer() {
    if (timerId) {
      clearTimeout(timerId)
      timerId = null
    }
  }

  function onActivity() {
    if (isLocked.value) return // Don't reset while locked
    
    // Check if token is already expired before resetting timer
    if (isJwtExpired()) {
      isLocked.value = true
      return
    }
    
    startTimer()
    sendActivityPing() // Notify backend of user activity
  }

  async function sendActivityPing() {
    try {
      const token = sessionStorage.getItem('userToken')
      if (!token) return // Skip if no token
      
      const apis = (await import("../apis/index")).default
      // Send lightweight heartbeat to backend
      // This keeps the backend session alive and syncs timeout
      await apis.validateSession()
    } catch (error) {
      // Silently fail - activity ping is not critical
    }
  }

  function resetTimer() {
    isLocked.value = false
    startTimer()
  }

  function addListeners() {
    EVENTS.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }))
  }

  function removeListeners() {
    EVENTS.forEach(evt => window.removeEventListener(evt, onActivity))
  }

  onMounted(() => {
    if (isAuthenticated.value) {
      addListeners()
      startTimer()
    }
  })

  onUnmounted(() => {
    clearTimer()
    removeListeners()
  })

  // Watch for login/logout to attach/detach listeners
  watch(isAuthenticated, (loggedIn) => {
    if (loggedIn) {
      isLocked.value = false
      addListeners()
      startTimer()
    } else {
      clearTimer()
      removeListeners()
      isLocked.value = false
    }
  })

  // Watch for external session expiration (e.g. from API 401)
  watch(() => store.state.sessionExpired, (expired) => {
    if (expired) {
      console.log('[IdleTimer] Session expired externally — locking screen');
      isLocked.value = true;
    }
  });

  return { isLocked, resetTimer }
}

